<script>
    import { createEventDispatcher } from "svelte";
    import { Section } from "../../lib/ui";
    import { PARAMS, PROGRESSION_PARAMS } from "./gameSlots.js";

    /** 저작자 조절값. 없는 키는 게임이 기본값으로 논다. */
    export let params = {};
    /** 성장 곡선(레벨·경험치). 난이도와 다른 컬럼이라 저장도 따로 나간다. */
    export let progression = {};

    const dispatch = createEventDispatcher();

    const valueOf = (p) => {
        const v = params?.[p.key];
        return typeof v === "number" && Number.isFinite(v) ? v : p.def;
    };

    const setValue = (p, raw) => {
        const v = Number(raw);
        if (!Number.isFinite(v)) return;
        dispatch("change", { ...params, [p.key]: v });
    };

    // 기본값으로 되돌리기 = 키를 지우는 것. 기본값을 저장해두면 나중에 우리가 기본을 바꿔도
    // 그 저작자만 옛 값에 묶인다(설계상 기본값은 코드가 갖는다).
    const resetOne = (p) => {
        const next = { ...params };
        delete next[p.key];
        dispatch("change", next);
    };

    const resetAll = () => dispatch("change", {});

    const fmt = (p, v) => (p.step < 1 ? v.toFixed(p.step < 0.1 ? 2 : 1) : String(v));
    const isChanged = (p) => params?.[p.key] !== undefined && params[p.key] !== p.def;

    $: changedCount = PARAMS.filter(isChanged).length;

    /* ── 성장 곡선 ─────────────────────────────────────────────────────────
     * ⚠️ 계산식은 런타임 progress.ts `levelFromScore` 와 같아야 한다 — 여기 미리보기와
     *    실제 게임의 레벨이 다르면 저작자는 어느 쪽을 믿어야 할지 알 수 없다. */
    const progValue = (p) => {
        const v = progression?.[p.key];
        return typeof v === "number" && Number.isFinite(v) ? v : p.def;
    };

    const setProg = (p, raw) => {
        const v = Number(raw);
        if (!Number.isFinite(v)) return;
        dispatch("progression", {
            ...progression,
            [p.key]: Math.min(Math.max(v, p.min), p.max),
        });
    };

    /** 레벨 n 에 닿기까지 필요한 **누적 점수**. 곡선을 숫자로만 보면 감이 안 온다. */
    const scoreForLevel = (level) => {
        const expPerScore = progValue(PROGRESSION_PARAMS[0]);
        const baseExp = progValue(PROGRESSION_PARAMS[1]);
        const growth = progValue(PROGRESSION_PARAMS[2]);
        let exp = 0;
        let need = baseExp;
        for (let i = 1; i < level; i++) {
            exp += need;
            need = Math.max(1, Math.floor(need * growth));
        }
        return Math.ceil(exp / expPerScore);
    };

    $: maxLevel = progValue(PROGRESSION_PARAMS[3]);
    $: previewLevels = [2, 5, 10, 30].filter((l) => l <= maxLevel);
</script>

<Section
    title="난이도"
    description="게임이 얼마나 빡세게 굴러갈지 정합니다. 시간이 지날수록 어려워지는 흐름은 그대로 두고, 그 세기만 조절합니다."
>
    <div class="tw-flex tw-flex-col tw-gap-5">
        {#each PARAMS as p (p.key)}
            <div>
                <div class="tw-mb-2 tw-flex tw-items-center tw-justify-between tw-gap-2">
                    <span class="tw-text-sm tw-font-semibold tw-text-foreground">{p.label}</span>
                    <span class="tw-flex tw-items-center tw-gap-2 tw-text-[13px] tw-font-bold tw-text-primary">
                        {fmt(p, valueOf(p))}{p.unit}
                        {#if isChanged(p)}
                            <button
                                type="button"
                                class="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-text-[11px] tw-font-normal tw-text-muted-foreground tw-underline"
                                on:click={() => resetOne(p)}>되돌리기</button
                            >
                        {/if}
                    </span>
                </div>
                <input
                    class="tw-w-full"
                    style="accent-color: hsl(var(--sb-primary))"
                    type="range"
                    min={p.min}
                    max={p.max}
                    step={p.step}
                    value={valueOf(p)}
                    on:change={(e) => setValue(p, e.target.value)}
                />
                <div class="tw-mt-1.5 tw-text-xs tw-text-muted-foreground">{p.hint}</div>
            </div>
        {/each}
    </div>

    {#if changedCount > 0}
        <div class="tw-mt-6 tw-border-t tw-border-border tw-pt-4">
            <button
                type="button"
                class="tw-cursor-pointer tw-rounded-md tw-border tw-border-border tw-bg-transparent tw-px-3.5 tw-py-2 tw-text-xs tw-text-muted-foreground tw-transition-colors hover:tw-text-foreground"
                on:click={resetAll}
            >
                전부 기본값으로 되돌리기 ({changedCount}개 변경됨)
            </button>
        </div>
    {/if}
</Section>

<Section
    title="성장 곡선"
    description="플레이어의 레벨이 오르는 속도입니다. 메인 화면 위쪽 레벨·경험치 막대에 나오고, 맵을 '레벨 도달'로 잠갔다면 그 판정에도 쓰입니다."
>
    <p class="tw-mb-4 tw-text-[13px] tw-leading-relaxed tw-text-muted-foreground">
        ⚠️ 경험치는 따로 쌓지 않고 <strong class="tw-text-foreground">누적 점수에서 계산</strong>합니다.
        그래서 공개한 뒤에 곡선을 크게 바꾸면 이미 논 사람의 레벨도 같이 움직입니다.
    </p>

    <div class="tw-flex tw-flex-wrap tw-gap-4">
        {#each PROGRESSION_PARAMS as p (p.key)}
            <label class="tw-flex tw-flex-col tw-gap-1.5">
                <span class="tw-text-[13px] tw-font-semibold tw-text-foreground">{p.label}</span>
                <input
                    class="tw-h-9 tw-w-[160px] tw-rounded-md tw-border tw-border-border tw-bg-background tw-px-3 tw-text-sm tw-text-foreground"
                    type="number"
                    min={p.min}
                    max={p.max}
                    step={p.step}
                    value={progValue(p)}
                    on:change={(e) => setProg(p, e.target.value)}
                />
                {#if p.hint}
                    <span class="tw-text-xs tw-text-muted-foreground">{p.hint}</span>
                {/if}
            </label>
        {/each}
    </div>

    <!-- 숫자만 보면 감이 안 온다 — 이 곡선이면 몇 점을 모아야 하는지 보여준다. -->
    <div class="tw-mt-5 tw-rounded-lg tw-border tw-border-border tw-bg-muted/40 tw-p-4">
        <div class="tw-mb-2 tw-text-[13px] tw-font-semibold tw-text-foreground">
            이 곡선이면 이렇게 됩니다
        </div>
        <div class="tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-1.5">
            {#each previewLevels as level (level)}
                <span class="tw-text-[13px] tw-text-muted-foreground">
                    레벨 {level} →
                    <strong class="tw-text-foreground">누적 {scoreForLevel(level).toLocaleString()}점</strong>
                </span>
            {/each}
        </div>
    </div>
</Section>
